import {
  ABANDONED_POWER_BP,
  BOSS_RELICS,
  CHALLENGE,
  GHOST_POWER_BP,
  LOSS_STORAGE_BP,
  LOSSES_TO_END,
  LATE_MAREAS,
  LAND_LEVELS,
  HALL,
  MERCENARY,
  LOOT_FAVOR,
  LOOT_POWER,
  MAREAS_PER_CAMPAIGN,
  STRUCTURES,
  WAVE_DUST_PER_PLAYER,
  WAVE,
  WAVES_PER_MAREA,
  XP,
  type NightmareType,
  type SeasonId,
  GREAT_WORK_LOSS_BP,
  AFFINITY_TYPES,
  ELIXIRS,
  OFFERINGS,
  DREAM_BEACON,
  type OfferingKind,
  ERA_NIGHTMARES,
  upToEra,
  PLAN,
  type DamageType,
  type HostId,
  type IncursionOmen,
  TOMES,
  GLYPH_FRAGS,
  UNSLEEPING,
  LAND,
} from '@somnhold/content';
import type { BattleParams, HeroInit, StructureInit } from '../battle/create.ts';
import { isBig } from '../land.ts';
import { cellsOf } from './footprint.ts';
import type { MapDef, PlanParams } from '../types.ts';
import { createBattle } from '../battle/create.ts';
import { runBattle } from '../battle/step.ts';
import { hexDist, hexRing, hexToWorld, parseHexKey } from '../hex.ts';
import { dist2, idiv, imax, imin, mulBp } from '../math/fixed.ts';
import { weakestRifts } from './rifts.ts';
import { deriveSeed, rngChance, rngInt } from '../math/rng.ts';
import { snapshot } from '../math/hash.ts';
import type { BattleState, PlayerId } from '../types.ts';
import { dominantType, generateWave, hostFor, planFor, type WaveSpec } from '../wave.ts';
import { activityWeight, addRes, builtMaxHp, event, keepMaxHp, scaleRes, sortedCellKeys, totalActivity, heroName } from './core.ts';
import { bestLevel, districtsAfterWave, eraOf, greatWork, groveAfterWave, hangTrophies, keepFrailBp, sanctumAfterWave, standing, trophyBp } from './eras.ts';
import { afflictAfter, carried, gearMods, grantXp, perkBp, addGlyphFragment, addPages } from './progress.ts';
import { giveItem, grantSeal, lookOf, lootCtx, lootRecipient, rollItem, rollSlot } from './items.ts';
import { grantRelic, skyRules } from './incursion.ts';
import { advanceTime } from './time.ts';
import { mercenaryPowerBp } from './commands.ts';
import { seasonAt } from '../season.ts';
import { afterUnsleeping, unsleepingFor } from './unsleeping.ts';
import { levelBudget, levelMarea, levelStrength, pressureAfter, pressureOf, startingPressure } from './level.ts';


import { risingOre } from './sides.ts';
import { unsleepingStood } from '../battle/unsleeping.ts';
import type { CampaignState, Ms, PlayerState, WaveRecord } from './types.ts';
import { fightDeeds, tallyDeeds } from './deeds.ts';
import { deepMarea, tallyFeats } from './feats.ts';

/** Which open sector a player's ghost defends in this wave (SPEC §9–11). */
export const ghostSectors = (s: CampaignState): Map<PlayerId, number> => {
  const out = new Map<PlayerId, number>();
  const n = s.map.sectors.length;
  const load = new Array<number>(n).fill(0);
  const anywhere: PlayerState[] = [];
  for (const p of s.players) {
    const post = p.ghostPost && 'sector' in p.ghostPost && p.ghostPost.sector >= n ? ({ anywhere: true } as const) : p.ghostPost;
    if (post && 'sector' in post && post.sector < n) {
      out.set(p.id, post.sector);
      load[post.sector] = (load[post.sector] as number) + 1;
    } else if (post && 'anywhere' in post) anywhere.push(p);
    else if (p.left || !post) {
      const sec = (p.adoptedSectors[0] ?? 0) % n;
      out.set(p.id, sec);
      load[sec] = (load[sec] as number) + 1;
    }
  }
  // "Wherever needed": fill the sector with the fewest defenders (lowest index on ties).
  for (const p of anywhere) {
    let best = 0;
    for (let i = 1; i < n; i++) if ((load[i] as number) < (load[best] as number)) best = i;
    out.set(p.id, best);
    load[best] = (load[best] as number) + 1;
  }
  return out;
};

export interface WavePlay {
  /** Players playing live (their heroes are 'live' in the battle; everyone else's is a ghost). */
  live?: PlayerId[];
  /** The omen over the wave (`waveSky`, from the day of its scheduled hour); absent or null: a clear sky. */
  sky?: IncursionOmen | null;
}

/**
 * A harder campaign's wave: the budget grows with the heroes' mean level and with what stands in
 * the bastion (structure levels), and the extra wall-breakers join the table.
 */
export const challengeSpec = (s: CampaignState): { budgetBp?: number; extraWeights?: Partial<Record<NightmareType, number>>; eliteFromMarea?: number } => {
  const active = s.players.filter((p) => !p.left);
  let levels = 0;
  for (const p of active) levels += p.hero.level;
  // Mean level above 1, in bp of a level.
  const above = active.length ? idiv((levels - active.length) * 10000, active.length) : 0;
  // Without a challenge the wave still grows a little with the heroes' level (WAVE.perLevelBp).
  if (!s.challenge) return WAVE.perLevelBp > 0 && above > 0 ? { budgetBp: 10000 + idiv(WAVE.perLevelBp * above, 10000) } : {};
  const c = CHALLENGE[s.challenge];
  let built = 0;
  for (const key of sortedCellKeys(s)) {
    const b = s.cells[key]?.built;
    // The ring every bastion starts with doesn't count: only what the group chose to build.
    if (b && b.structure !== 'rampart' && (STRUCTURES[b.structure].hp <= 0 || b.hp > 0)) built += b.level;
  }
  const levelBp = 10000 + idiv(c.perLevelBp * above, 10000);
  const buildBp = 10000 + imin(c.maxBuildBp, c.perBuildBp * built);
  return { budgetBp: mulBp(mulBp(c.budgetBp, levelBp), buildBp), extraWeights: c.extraWeights, eliteFromMarea: c.eliteFromMarea };
};

/**
 * The battle of the current wave, one for the whole bastion: every entrance's nightmares (some
 * across the rough land from any side), every structure and every hero — live if playing, else a
 * ghost at its post.
 */
/**
 * The era's nightmares join the table (on top of a harder campaign's extra ones); a won sappers' fort
 * keeps sappers out of the next wave.
 */
const eraNightmares = (s: CampaignState, marea: number, wave: number): { extraWeights?: Partial<Record<NightmareType, number>> } => {
  const add = upToEra(ERA_NIGHTMARES, eraOf(s));
  const base = challengeSpec(s).extraWeights;
  if (!add && !base) return {};
  const out: Partial<Record<NightmareType, number>> = { ...(base ?? {}) };
  for (const k of Object.keys(add ?? {}).sort() as NightmareType[]) out[k] = (out[k] ?? 0) + (add?.[k] ?? 0);
  if (s.sappersCleared && s.sappersCleared.marea === marea && s.sappersCleared.wave === wave) delete out.sapper;
  return { extraWeights: out };
};

/** The challenge's spec, with the budget raised if this is the wave an admin surged, and eased if its Marea was relieved. */
const surged = (s: CampaignState, marea: number, wave: number): ReturnType<typeof challengeSpec> => {
  let c = challengeSpec(s);
  const rl = s.relief;
  if (rl && rl.marea === marea) c = { ...c, budgetBp: mulBp(c.budgetBp ?? 10000, rl.budgetBp) };
  const sg = s.surge;
  if (!sg || sg.marea !== marea || sg.wave !== wave) return c;
  return { ...c, budgetBp: mulBp(c.budgetBp ?? 10000, sg.budgetBp) };
};

/**
 * Where a flanking group storms the wall: the cell outside it 2 to 5 cells to one side of its gate, along
 * the ring (`flank` picks the side and how far).
 */
const flankRally = (s: CampaignState, outside: { q: number; r: number }[], sector: number, flank: number, map: MapDef = s.map): { q: number; r: number } => {
  const path = map.sectors[sector]?.path ?? [];
  const door = path[path.length - 1] ?? { q: 0, r: 0 };
  let at = 0;
  for (let i = 1; i < outside.length; i++) if (hexDist(outside[i] as { q: number; r: number }, door) < hexDist(outside[at] as { q: number; r: number }, door)) at = i;
  const span = WAVE.flankMax - WAVE.flankMin + 1;
  const far = WAVE.flankMin + idiv((flank % 5000) * span, 5000);
  const side = flank < 5000 ? -1 : 1;
  // A wrap that holds however short the skirt is (on terraced land it can be two cells): the same cell as before
  // whenever the old sum stayed at or above zero.
  const n = outside.length;
  return outside[(((at + side * far) % n) + n) % n] as { q: number; r: number };
};

/**
 * Where a plan's squads wait and strike (docs/TACTICAS.md). The bulk waits on its path at the cell nearest
 * the gate that every tower is at least `PLAN.stageClearance` away from (the path's start if none is); a
 * feint's bulk strikes the wall a few cells to one side of the gate, like a flanking group.
 */
const planParams = (s: CampaignState, comp: { plan: PlanParams['id'] | null; squads: { role: PlanParams['squads'][number]['role']; sector: number }[] }, structures: StructureInit[], marea: number, wave: number, map: MapDef = s.map): PlanParams | null => {
  if (!comp.plan) return null;
  const towers = structures.filter((x) => STRUCTURES[x.type].category === 'tower').map((x) => hexToWorld(x.q, x.r));
  const far = PLAN.stageClearance * PLAN.stageClearance;
  // Terraced land: the ground just outside the walls the group raised, from each fissure's way to its wall
  // (docs/CONSTRUCCION.md §6); with no walls closed, round the crest.
  const skirt = map.land?.skirt ?? [];
  const outside = map.land ? (skirt.length > 0 ? skirt : hexRing(LAND.crest + 2)) : hexRing(map.bastion + 1);
  const stageOf = (sector: number): { q: number; r: number } => {
    const path = map.sectors[sector]?.path ?? [];
    for (let i = path.length - 1; i >= 0; i--) {
      const c = path[i] as { q: number; r: number };
      const w = hexToWorld(c.q, c.r);
      if (towers.every((t) => dist2(t.x, t.y, w.x, w.y) >= far)) return c;
    }
    return path[0] ?? { q: 0, r: 0 };
  };
  // Each sector's flanks, the same distance from the gate on each side. A feint's bulk storms one of them
  // (drawn); a pincer's halves, both.
  const flankDraw = (sector: number): number => deriveSeed(waveSeed(s), 70000 + marea * 16 + wave * 4 + sector) % 10000;
  const strikeOf = (plan: PlanParams['id'], role: string, sector: number): { strike?: { q: number; r: number } } => {
    const d = flankDraw(sector);
    if (plan === 'feint' && role === 'main') return { strike: flankRally(s, outside, sector, d, map) };
    if (plan === 'pincer' && (role === 'main' || role === 'second')) return { strike: flankRally(s, outside, sector, (d % 5000) + (role === 'main' ? 0 : 5000), map) };
    return {};
  };
  return {
    id: comp.plan,
    fronts: map.sectors.map((_, i) => {
      const d = flankDraw(i) % 5000;
      return { stage: stageOf(i), left: flankRally(s, outside, i, d, map), right: flankRally(s, outside, i, d + 5000, map) };
    }),
    squads: comp.squads.map((q) => ({
      role: q.role,
      sector: q.sector,
      stage: stageOf(q.sector),
      ...strikeOf(comp.plan as PlanParams['id'], q.role, q.sector),
    })),
  };
};

/** The season field of a battle fought at `now` (absent on an ordinary day). */
export const seasonOf = (now: Ms): { season?: SeasonId } => {
  const id = seasonAt(now);
  return id ? { season: id } : {};
};

/** The offerings burned at the keep that bless this Marea's waves. */
export const offeringsFor = (s: CampaignState, marea: number): OfferingKind[] => (s.offerings && s.offerings.marea === marea ? s.offerings.kinds : []);

/** What a bastion wave of the campaign is drawn from (its host, its table, its budget). */
/** The seed a Marea's waves grow from: the campaign's, drawn anew each time a lost late Marea is played again. */
export const waveSeed = (s: CampaignState): number => (s.mareaRepeats ? deriveSeed(s.seed, 80000 + s.mareaRepeats) : s.seed);

/** The calendar slot of the current Marea: its index, shifted by the Mareas played again. */
export const calendarMarea = (s: CampaignState): number => s.mareaIndex + (s.mareaRepeats ?? 0);

/**
 * `live`: how many heroes are played live in the fight. Each adds `WAVE.liveBp` to the budget (people play far
 * better than their ghosts); the omen and the offline wave are drawn with none.
 */
const levelBudgetSpec = (s: CampaignState, step: number, byLevel: boolean, spec: WaveSpec): WaveSpec =>
  byLevel && levelBudget(s, step) > 10000 ? { ...spec, budgetBp: mulBp(spec.budgetBp ?? 10000, levelBudget(s, step)) } : spec;

/** The campaign's pressure on a bastion wave's budget (PRESSURE): on the waves played live, or its ghosts'. */
const pressureSpec = (s: CampaignState, marea: number, wave: number, live: number, byLevel: boolean, spec: WaveSpec): WaveSpec => {
  if (!byLevel) return spec;
  const p = pressureOf(s, live > 0, marea, wave);
  return p === 10000 ? spec : { ...spec, budgetBp: mulBp(spec.budgetBp ?? 10000, p), bossBp: p };
};

/**
 * `byLevel`: false for a fight that borrows the bastion's wave but has its own scale (a side saga's night): it keeps
 * the calendar's Marea. A bastion wave is measured by the heroes' level and the campaign's pressure (campaign/level.ts).
 */
export const waveSpecOf = (s: CampaignState, now: Ms, marea: number, wave: number, live = 0, byLevel = true): WaveSpec => {
  const step = byLevel ? levelMarea(s) : marea;
  return liveSpec(live, pressureSpec(s, marea, wave, live, byLevel, levelBudgetSpec(s, step, byLevel, {
    seed: waveSeed(s),
    marea,
    wave,
    sectors: s.map.sectors.length,
    activityBp: totalActivity(s, now),
    depth: s.depth,
    lossPenalty: s.lossPenalty,
    wildBp: WAVE.wildBp,
    flankBp: step >= WAVE.flankFromMarea ? WAVE.flankBp : 0,
    hosts: true,
    plans: true,
    ...(s.planForce && s.planForce.marea === marea && s.planForce.wave === wave ? { plan: s.planForce.plan } : {}),
    ...(s.draft && s.draft.marea === marea && s.draft.wave === wave ? { army: s.draft.army } : {}),
    ...unsleepingBudget(s, marea, wave, surged(s, marea, wave)),
    ...eraNightmares(s, marea, wave),
    ...unsleepingFor(s, marea, wave),
    ...(byLevel ? { tableMarea: step } : {}),
    ...(byLevel && levelStrength(s, step) > 10000 ? { levelStrengthBp: levelStrength(s, step) } : {}),
  })));
};

/** With one of the Unsleeping coming, the rest of the wave is smaller: he is his own share of the budget. */
const unsleepingBudget = (s: CampaignState, marea: number, wave: number, c: ReturnType<typeof challengeSpec>): ReturnType<typeof challengeSpec> =>
  unsleepingFor(s, marea, wave).unsleeping ? { ...c, budgetBp: mulBp(c.budgetBp ?? 10000, UNSLEEPING.waveBudgetBp) } : c;

const liveSpec = (live: number, spec: WaveSpec): WaveSpec => (live > 0 ? { ...spec, budgetBp: mulBp(spec.budgetBp ?? 10000, 10000 + live * WAVE.liveBp) } : spec);

/** The war host a bastion wave brings (the omen names it ahead). */

export const waveHost = (s: CampaignState, now: Ms, marea: number, wave: number): HostId => hostFor(waveSpecOf(s, now, marea, wave));
/** The battle plan a bastion wave brings (the omen names it too), or null. */
export const wavePlan = (s: CampaignState, now: Ms, marea: number, wave: number): PlanParams['id'] | null => planFor(waveSpecOf(s, now, marea, wave));
/** The damage type the wave mostly brings (the omen names it), or null. */
export const waveDamageType = (s: CampaignState, now: Ms, marea: number, wave: number): DamageType | null => dominantType(generateWave(waveSpecOf(s, now, marea, wave)).spawns);

export const waveBattleParams = (
  s: CampaignState,
  now: Ms,
  opts: WavePlay & { marea?: number; wave?: number; mode?: 'wave' | 'practice'; byLevel?: boolean } = {},
): BattleParams => {
  const marea = opts.marea ?? s.mareaIndex;
  const wave = opts.wave ?? s.waveIndex;
  const live = new Set(opts.live ?? []);
  const comp = generateWave(waveSpecOf(s, now, marea, wave, s.players.filter((p) => live.has(p.id)).length, opts.byLevel ?? true));
  // Groups from the wild come out at a point on the edge of the land.
  const edge = hexRing(s.map.radius);
  const outside = hexRing(s.map.bastion + 1);
  const spawns = comp.spawns.map((x) => {
    // Terraced land: everything climbs from the zones on the rim (no rough land round a ring, no flanks of a gate).
    if (s.map.land) {
      const { edge: _edge, flank: _flank, ...rest } = x;
      return rest;
    }
    if (x.edge !== undefined) return { ...x, at: edge[idiv(x.edge * edge.length, 10000)] as { q: number; r: number } };
    if (x.flank !== undefined) return { ...x, rally: flankRally(s, outside, x.sector, x.flank) };
    return x;
  });
  const structures: StructureInit[] = [];
  for (const key of sortedCellKeys(s)) {
    const b = s.cells[key]?.built;
    if (!b) continue;
    const h = parseHexKey(key);
    structures.push({
      type: b.structure,
      level: b.level,
      material: b.material,
      q: h.q,
      r: h.r,
      hp: STRUCTURES[b.structure].hp > 0 ? b.hp : undefined,
      enchant: b.enchant && b.enchant.until > now ? b.enchant.element : null,
      fortified: b.fortifiedUntil > now,
      charges: b.charges,
      cellKey: key,
      // A three-cell building on terraced land blocks all three.
      ...(s.map.land && isBig(b.structure) ? { foot: cellsOf(s, key).slice(1) } : {}),
      ...(s.map.land && b.branch ? { branch: b.branch } : {}),
      ...(s.map.land && b.mount ? { mount: { type: b.mount.type, level: b.mount.level } } : {}),
    });
  }
  // Terraced land: the wave cracks the field open where nobody could tell, where the bastion is weakest, and
  // comes out of it (docs/CONSTRUCCION.md §6).
  const map = s.map.land ? weakestRifts(s.map, structures, deriveSeed(waveSeed(s), marea * 100 + wave * 10 + 9)) : s.map;
  const plan = planParams(s, comp, structures, marea, wave, map);
  const posts = ghostSectors(s);
  const brewed = s.elixir && s.elixir.marea === marea && s.elixir.wave === wave ? s.elixir : null;
  // On terraced land the laboratory's level makes its elixirs stronger (LAND_LEVELS.lab).
  const brewBp = s.map.land ? (LAND_LEVELS.lab.elixirBp[bestLevel(s, 'lab') - 1] ?? 10000) : 10000;
  const elixirs = brewed ? [brewed.kind, ...(brewed.also ? [brewed.also] : [])].map((k) => ELIXIRS[k]) : [];
  const wardType = elixirs.some((x) => x.resBp) ? dominantType(generateWave(waveSpecOf(s, now, marea, wave, 0, opts.byLevel ?? true)).spawns) : null;
  // The clan hall's trophies of this Marea: every hero hits harder.
  const trophies = trophyBp(s, marea);
  // The offerings burned at the keep for this Marea.
  const offered = offeringsFor(s, marea);
  const vigil = offered.includes('vigil') ? OFFERINGS.vigil.ghostPowerBp : 0;
  const rules = {
    // The day's omen, where it changes waves (docs/PRESAGIOS.md).
    ...(opts.sky ? skyRules(opts.sky) : {}),
    ...(keepFrailBp(s) !== 10000 ? { keepFrailBp: keepFrailBp(s) } : {}),
    ...(offered.includes('oil') ? { towerDamageBp: OFFERINGS.oil.towerDamageBp } : {}),
    ...(offered.includes('mortar') ? { structureTakenBp: OFFERINGS.mortar.structureTakenBp } : {}),
    // Era V: the Dream Beacon on the crowned keep.
    ...(eraOf(s) >= 5 ? { dreamBeacon: DREAM_BEACON } : {}),
    // Terraced land: the general cracks new fissures open mid-wave where the heroes aren't (battle/rifts.ts). Not in
    // practice, nor in a side saga's night (its own scale).
    ...(s.map.land && (opts.mode ?? 'wave') === 'wave' && (opts.byLevel ?? true) ? { rifts: true } : {}),
  };
  const heroes: HeroInit[] = [];
  for (const p of s.players) {
    const isLive = live.has(p.id);
    const post = posts.get(p.id) ?? 0;
    const roam = !p.left && !!p.ghostPost && ('anywhere' in p.ghostPost || p.ghostPost.sector >= s.map.sectors.length);
    const act = p.left ? ABANDONED_POWER_BP : activityWeight(p, now);
    const weight = p.left ? ABANDONED_POWER_BP : mulBp(mulBp(GHOST_POWER_BP, 10000 + perkBp(p.hero, 'ghostPower') + vigil), act);
    if (!isLive && weight <= 0) continue;
    const gear = gearMods(p.hero);
    // The laboratory's elixir for this wave: everyone who fights it drinks.
    for (const elixir of elixirs) {
      gear.damageBp += mulBp(elixir.damageBp ?? 0, brewBp);
      gear.hpBp += mulBp(elixir.hpBp ?? 0, brewBp);
      // The warding elixir: against what the wave mostly brings, or a little against everything.
      if (elixir.resBp || elixir.allBp) {
        const r = (gear.resBp ??= {});
        if (wardType) r[wardType] = (r[wardType] ?? 0) + mulBp(elixir.resBp ?? 0, brewBp);
        else for (const k of AFFINITY_TYPES) r[k] = (r[k] ?? 0) + mulBp(elixir.allBp ?? 0, brewBp);
      }
    }
    gear.damageBp += trophies;
    // A fading ghost is thinner too: its health follows the same activity weight (design doc: "pierden fuerza").
    if (!isLive) gear.hpBp = mulBp(10000 + gear.hpBp, act) - 10000;
    heroes.push({
      pid: p.id,
      name: heroName(p),
      cls: p.hero.heroClass,
      level: p.hero.level,
      control: isLive ? 'live' : 'ghost',
      powerBp: isLive ? 10000 : weight,
      post,
      ...(roam ? { roam: true } : {}),
      profile: p.ghostProfile,
      relics: p.hero.relics, awakened: p.hero.awakened ?? [],
      gear,
      ...lookOf(p.hero),
      ...carried(p.hero, 'wave', s),
      ...(isLive ? {} : { presenceBp: act }),
    });
  }
  const mercPower = mercenaryPowerBp(s);
  let mi = 0;
  for (const merc of s.mercs ?? []) {
    if (merc.marea < marea) continue;
    heroes.push({
      pid: `merc:${mi}`,
      name: '',
      cls: merc.cls,
      level: imin(11, MERCENARY.level + MERCENARY.levelPerMarea * marea),
      control: 'merc',
      powerBp: mercPower,
      post: mi % imax(1, s.map.sectors.length),
      roam: true,
      gear: { hpBp: mercPower - 10000, damageBp: trophies },
    });
    mi++;
  }
  // A copy that shares nothing with the campaign: the replay is written later, after the state moved on.
  return snapshot({
    mode: opts.mode ?? 'wave',
    seed: deriveSeed(waveSeed(s), marea * 100 + wave * 10 + 7),
    map,
    structures,
    heroes,
    keepHp: s.keep.hp,
    keepMaxHp: s.keep.maxHp,
    marea,
    wave,
    spawns,
    bossHp: comp.boss ? comp.bossHp : 0,
    ...(plan ? { plan } : {}),
    ...seasonOf(now),
    // The clan hall's war drums, when a barbarian fights the wave.
    ...(standing(s, 'hall') && heroes.some((h) => h.cls === 'barbarian') ? { drums: s.map.land && bestLevel(s, 'hall') >= 3 ? mulBp(HALL.drumsTicks, LAND_LEVELS.hall.drumsBp[2] ?? 10000) : true } : {}),
    ...(Object.keys(rules).length ? { rules } : {}),
  });
};

export const waveBattle = (s: CampaignState, now: Ms, opts: WavePlay = {}): BattleState => createBattle(waveBattleParams(s, now, opts));

/** Writes a finished wave battle back into the campaign: structure HP, trap charges, keep HP. */
export const applyWaveResult = (s: CampaignState, b: BattleState): void => {
  for (const st of b.structures) {
    const cell = s.cells[st.cellKey];
    const built = cell?.built;
    if (!built || built.structure !== st.type) continue;
    if (STRUCTURES[st.type].hp > 0) built.hp = st.destroyed ? 0 : st.hp;
    if (STRUCTURES[st.type].category === 'trap') built.charges = st.charges;
  }
  s.keep.hp = b.keep.hp < 0 ? 0 : b.keep.hp;
};

export interface WaveInput {
  /** The wave's battle if it was played live; otherwise it is simulated with everyone's ghosts. */
  played?: { battle: BattleState; source: 'live' | 'preview' };
  /** Called with the parameters of the battle when it is simulated offline (replays). */
  onOffline?: (params: BattleParams, battle: BattleState) => void;
  /** The omen over the wave, for the one simulated offline (WavePlay.sky). */
  sky?: IncursionOmen | null;
}

/** Resolves the current wave at its scheduled time (SPEC §8.3): one battle for the whole bastion. */
export const resolveWave = (s: CampaignState, now: Ms, input: WaveInput = {}): WaveRecord | null => {
  if (s.status !== 'active') return null;
  advanceTime(s, now);
  const marea = s.mareaIndex;
  const wave = s.waveIndex;
  if (wave === 0) startMarea(s, now);
  const rec: WaveRecord = { marea, wave, at: now, sectors: [], keepHp: 0, keepMaxHp: s.keep.maxHp, result: 'won', dust: 0, highlights: [] };
  // The pressure starts from what the calendar's rule weighed, before this wave's experience moves the level.
  if (!s.pressure) {
    const p = startingPressure(s, marea, wave);
    s.pressure = { live: p, ghost: p };
  }
  let pressureHurt: number | null = null;
  if (s.mareaRepeats) rec.slot = calendarMarea(s);
  // What each hero took from the wave, kept on its record so the report can show it.
  const earned = (pid: PlayerId, n: number): void => {
    if (n > 0) (rec.xp ??= {})[pid] = (rec.xp[pid] ?? 0) + n;
  };
  const participants = new Set<PlayerId>();
  const liveParticipants = new Set<PlayerId>();
  let bossKilled = '';
  let unsleepingFelled = false;
  if (!s.mareaLost) {
    // One fight for the whole bastion: played live, or simulated with everyone's ghosts.
    let battle: BattleState;
    let source: 'live' | 'preview' | 'offline' = 'offline';
    if (input.played) {
      battle = input.played.battle;
      source = input.played.source;
      // The real keep HP comes from this resolution, not from when the battle was played:
      // only what this fight did to it (the battle already started from a hurt keep).
      battle.keep.hp = s.keep.hp - battle.stats.keepDamage;
    } else {
      const params = waveBattleParams(s, now, { sky: input.sky ?? null });
      battle = createBattle(params);
      runBattle(battle);
      input.onOffline?.(params, battle);
    }
    // A wave under an omen's sky moves no pressure, up or down: the next one, under a clear sky, would pay for it
    // (docs/PRESAGIOS.md §7).
    if (battle.rules?.omen) rec.sky = { omen: battle.rules.omen, ...(battle.stats.sky ? { bolts: battle.stats.sky.bolts, rods: battle.stats.sky.rods } : {}) };
    if (!battle.rules?.omen) pressureHurt = idiv(battle.stats.keepDamage * 10000, s.keep.maxHp > 0 ? s.keep.maxHp : 1);
    applyWaveResult(s, battle);
    afflictAfter(s, battle, now);
    // The elixir was drunk.
    if (s.elixir && s.elixir.marea === marea && s.elixir.wave === wave) delete s.elixir;
    const quarry = districtsAfterWave(s, (key) => {
      const b = s.cells[key]?.built;
      return b ? builtMaxHp(b, now) : 0;
    });
    const grove = groveAfterWave(s);
    const mended = sanctumAfterWave(s, (key) => {
      const b = s.cells[key]?.built;
      return b ? builtMaxHp(b, now) : 0;
    });
    if (quarry) (rec.yields ??= {}).quarry = quarry;
    if (grove > 0) (rec.yields ??= {}).grove = grove;
    if (mended > 0) (rec.yields ??= {}).sanctum = mended;
    hangTrophies(s, marea, battle.stats.highlights.filter((hl) => hl.kind === 'eliteSlain' || hl.kind === 'bossSlain').length);
    for (const h of battle.heroes) {
      participants.add(h.pid);
      if (h.control === 'live') liveParticipants.add(h.pid);
    }
    // Warlords always drop a piece: it goes to whoever needs it most among those who fought (live first).
    const fighters = battle.heroes.map((h) => h.pid);
    for (const hl of battle.stats.highlights) {
      if (hl.kind !== 'eliteSlain' && hl.kind !== 'bossSlain') continue;
      // The slot first (any open to the highest of them), then who needs it most, then a piece for that hero's class.
      let top = 1;
      for (const id of fighters) top = imax(top, s.players.find((x) => x.id === id)?.hero.level ?? 1);
      // Past the sixth Tide, warlords and bosses drop more rings (docs/EQUIPO.md §6).
      const deep = imax(0, marea - (MAREAS_PER_CAMPAIGN - 1));
      const slot = rollSlot(s.rng, top, deep > 0 ? LOOT_FAVOR.tide : undefined);
      const pid = lootRecipient(s, { slot }, fighters, liveParticipants);
      const p = s.players.find((x) => x.id === pid);
      if (p) {
        giveItem(s, p, rollItem(s, s.rng, hl.kind === 'bossSlain' ? 'rare' : undefined, lootCtx(s, p, LOOT_POWER.deepMarea * deep, deep > 0 ? 'tide' : undefined, undefined, slot)), now);
        p.hero.warlordLoot = (p.hero.warlordLoot ?? 0) + 1;
      }
      // An ink warlord sometimes leaves the Dreamer's Ring behind.
      if (p && hl.kind === 'eliteSlain' && hl.ink && rngChance(s.rng, 1500)) grantRelic(s, p.id, 'dreamerRing', now);
    }
    if (battle.stats.bossKilled) {
      const boss = battle.wave.spawns.find((x) => x.kind === 'witch' || x.kind === 'shell');
      if (boss) bossKilled = boss.kind;
    }
    // One of the Unsleeping (docs/ANTIHEROES.md): what the campaign keeps of the fight (his felling drops a rare
    // piece above, like a boss's).
    unsleepingStood(battle);
    const un = battle.stats.unsleeping;
    if (un) {
      rec.unsleeping = { id: un.id, outcome: un.outcome, ...(un.pid ? { pid: un.pid } : {}) };
      if (afterUnsleeping(s, now, marea, wave, un, battle.heroes.map((h) => ({ pid: h.pid, live: h.control === 'live' })))) unsleepingFelled = true;
    }
    // The report still tells each entrance apart (kills, leaks, losses), from the one fight.
    for (const sec of s.map.sectors) {
      const ss = battle.stats.sectors[sec.index];
      rec.sectors.push({
        index: sec.index,
        result: battle.result === 'lost' || s.keep.hp <= 0 ? 'lost' : 'won',
        source,
        kills: ss?.kills ?? 0,
        leaks: ss?.leaks ?? 0,
        lost: ss?.lost ?? 0,
        gateFell: ss?.gateFell ?? false,
        defenders: battle.heroes.filter((h) => h.post === sec.index).map((h) => h.pid),
        keepDamage: sec.index === 0 ? battle.stats.keepDamage : 0,
        seed: battle.seed,
      });
    }
    // Terraced land: where it came out of the ground, for the report.
    if (battle.map.land?.open) rec.rifts = battle.map.sectors.map((sec) => ({ q: (sec.path[0] ?? sec.gate).q, r: (sec.path[0] ?? sec.gate).r }));
    rec.deeds = fightDeeds(battle);
    tallyDeeds(s, 'wave', battle, now);
    // The unique pieces' feats (docs/UNICAS.md §2.3): the late Mareas are hard places, and their bosses count.
    const gains = tallyFeats(s, battle, { hard: deepMarea(marea), deepMarea: deepMarea(marea) }, now);
    if (gains.length) rec.feats = [...(rec.feats ?? []), ...gains];
    rec.dust += battle.stats.dust;
    for (const hl of battle.stats.highlights) {
      if (rec.highlights.length < 24 && hl.kind !== 'heroDown') rec.highlights.push(`${hl.kind}:${hl.pid ?? ''}:${hl.sector ?? 0}:${hl.what ?? ''}`);
    }
  }
  rec.keepHp = s.keep.hp;
  if (participants.size) rec.live = [...liveParticipants].sort();
  rec.result = s.keep.hp > 0 && !s.mareaLost ? 'won' : 'lost';
  // What the wave did moves the pressure of its kind (played live or by the ghosts alone).
  if (pressureHurt !== null) {
    const key = liveParticipants.size > 0 ? 'live' : 'ghost';
    s.pressure[key] = pressureAfter(s.pressure[key], rec.result === 'lost', pressureHurt);
  }
  // Rewards for a defended wave.
  if (rec.result === 'won') {
    rec.dust += WAVE_DUST_PER_PLAYER * participants.size;
    s.storage.dust += rec.dust;
    for (const pid of participants) earned(pid, grantXp(s, pid, liveParticipants.has(pid) ? XP.liveWave : XP.ghostWave, now));
  }
  // An Unsleeping felled for good: everyone who fought him learns from it, and the storehouse gets his dust.
  if (unsleepingFelled) {
    s.storage.dust += UNSLEEPING.felledDust;
    rec.dust += UNSLEEPING.felledDust;
    for (const pid of participants) earned(pid, grantXp(s, pid, XP.boss, now));
  }
  // Every boss felled pays (the late Mareas bring them back); the first of each is remembered.
  if (bossKilled) {
    if (!s.bossesDefeated.includes(bossKilled)) s.bossesDefeated.push(bossKilled);
    const relics = BOSS_RELICS[bossKilled as 'witch' | 'shell'];
    for (const pid of participants) {
      const p = s.players.find((x) => x.id === pid);
      if (!p) continue;
      earned(pid, grantXp(s, pid, XP.boss, now));
      addPages(s, pid, 2, TOMES.boss, now);
      // And a fragment of a conversion glyph (docs/GLIFOS.md §3).
      addGlyphFragment(s, pid, 'boss', GLYPH_FRAGS.boss, now);
      // A boss of the late Mareas: the seal of a unique's third awakening.
      if (marea >= LATE_MAREAS.bossFrom) grantSeal(s, p, 'tide', now);
      // One of the boss's relics the hero doesn't have: carried, or kept if both slots are full.
      const choices = relics.filter((r) => !p.hero.relics.includes(r) && !(p.hero.relicBag ?? []).includes(r));
      if (choices.length > 0) grantRelic(s, pid, choices[rngInt(s.rng, choices.length)] as (typeof relics)[number], now);
    }
  }
  s.waves.push(rec);
  if (s.waves.length > 60) s.waves.splice(0, s.waves.length - 60);
  event(s, now, 'wave', { data: { marea, wave, result: rec.result, keep: s.keep.hp } });
  if (!s.mareaLost && s.keep.hp <= 0) loseMarea(s, now);
  // Advance the schedule.
  s.waveIndex++;
  if (s.waveIndex >= WAVES_PER_MAREA && s.status === 'active') {
    const lost = s.mareaLost;
    if (!lost) winMarea(s, now);
    s.waveIndex = 0;
    s.mareaLost = false;
    // A lost late Marea comes again; any other moves on.
    if (lost && s.mareaIndex >= LATE_MAREAS.repeatFrom) repeatMarea(s, now);
    else s.mareaIndex++;
    // Las vías 4, the rails left open: the week's ore comes up.
    risingOre(s, now);
    // Rebuilt for the next Marea right away: battles played before its first wave starts need it standing.
    const max = keepMaxHp(s, now);
    s.keep = { hp: max, maxHp: max };
    // The Hollow Shell beaten: the first season is won, and the tide goes on.
    if (!lost && s.mareaIndex === MAREAS_PER_CAMPAIGN && !s.seasonWon) {
      s.seasonWon = true;
      for (const p of s.players) grantXp(s, p.id, XP.campaignWon, now);
      event(s, now, 'campaignWon');
    }
  }
  s.version++;
  return rec;
};

/** Start of a Marea: the keep is restored to full (the entrances stay as they were laid out). */
const startMarea = (s: CampaignState, now: Ms): void => {
  const max = keepMaxHp(s, now);
  s.keep = { hp: max, maxHp: max };
};

const loseMarea = (s: CampaignState, now: Ms): void => {
  s.mareaLost = true;
  s.consecutiveLosses++;
  // A late Marea is played again instead: no harder next Marea, no end; every second loss in a row the titan stirs.
  if (s.mareaIndex >= LATE_MAREAS.repeatFrom) {
    s.lossPenalty = false;
    event(s, now, 'mareaLost', { data: { marea: s.mareaIndex } });
    if (s.consecutiveLosses % 2 === 0) titanStirs(s, now);
    return;
  }
  s.lossPenalty = true;
  const lost = scaleRes(s.storage, LOSS_STORAGE_BP);
  addRes(s.storage, lost, -1);
  shakeGreatWork(s);
  event(s, now, 'mareaLost', { data: { marea: s.mareaIndex } });
  if (s.consecutiveLosses >= LOSSES_TO_END) {
    s.status = 'lost';
    event(s, now, 'campaignLost');
  }
};

/** The titan stirs in its sleep: everything built is shaken to half its life, the storage spills a share, a Great Work loses a quarter. */
const titanStirs = (s: CampaignState, now: Ms): void => {
  const st = LATE_MAREAS.stir;
  for (const key of sortedCellKeys(s)) {
    const b = s.cells[key]?.built;
    if (!b || b.hp <= 0) continue;
    const half = mulBp(builtMaxHp(b, now), st.structureHpBp);
    if (b.hp > half) b.hp = half;
  }
  addRes(s.storage, scaleRes(s.storage, st.storageBp), -1);
  shakeGreatWork(s);
  event(s, now, 'titanStirs', { data: { marea: s.mareaIndex } });
};

/** A lost late Marea comes again: the calendar makes room for it and its waves are drawn anew. */
const repeatMarea = (s: CampaignState, now: Ms): void => {
  s.mareaRepeats = (s.mareaRepeats ?? 0) + 1;
  // What was planned against the lost run's waves doesn't carry over.
  delete s.draft;
  delete s.surge;
  delete s.sappersCleared;
  delete s.elixir;
  event(s, now, 'mareaRepeated', { data: { marea: s.mareaIndex } });
};

/** A Great Work under way loses a quarter: of what was paid, or of the work done. */
const shakeGreatWork = (s: CampaignState): void => {
  const gw = greatWork(s);
  if (!gw) return;
  if (gw.stage === 'blueprint') gw.paid = scaleRes(gw.paid, 10000 - GREAT_WORK_LOSS_BP);
  else gw.progressMs = mulBp(gw.progressMs, 10000 - GREAT_WORK_LOSS_BP);
};

const winMarea = (s: CampaignState, now: Ms): void => {
  s.consecutiveLosses = 0;
  s.lossPenalty = false;
  event(s, now, 'mareaWon', { data: { marea: s.mareaIndex } });
};
